/**
 * PriceAlertDialog — create a manual price alert ("notify when SYMBOL goes
 * above/below X"). Reusable from the Alerts page (and later the chart/watchlist).
 * Posts to /alerts/price; the bounded priceAlertMonitor fires it.
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  ToggleButton, ToggleButtonGroup, Typography, Box,
} from '@mui/material';
import toast from 'react-hot-toast';
import SymbolAutocomplete from '../common/SymbolAutocomplete';
import { tradingService } from '../../services/trading.service';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated?: () => void;
  /** Optional pre-fill (when opened from a chart/watchlist row). */
  initialSymbol?: string;
  initialExchange?: string;
  initialPrice?: number;
}

const PriceAlertDialog: React.FC<Props> = ({ open, onClose, onCreated, initialSymbol, initialExchange, initialPrice }) => {
  const [symbol, setSymbol] = useState(initialSymbol || '');
  const [exchange, setExchange] = useState(initialExchange || '');
  const [comparator, setComparator] = useState<'above' | 'below'>('above');
  const [price, setPrice] = useState<string>(initialPrice != null ? String(initialPrice) : '');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setSymbol(initialSymbol || ''); setExchange(initialExchange || '');
      setPrice(initialPrice != null ? String(initialPrice) : ''); setComparator('above');
    }
  }, [open, initialSymbol, initialExchange, initialPrice]);

  const submit = async () => {
    const p = Number(price);
    if (!symbol || !exchange || !(p > 0)) { toast.error('Pick a symbol and a positive price.'); return; }
    setSubmitting(true);
    try {
      await tradingService.createPriceAlert({ symbol, exchange, comparator, price: p });
      toast.success(`Alert set — ${symbol} ${comparator} ${p}`);
      onCreated?.();
      onClose();
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || 'Failed to create price alert');
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Set price alert</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Get notified (in-app + Telegram) when a symbol crosses your level. Checked every ~30s.
        </Typography>
        <Box sx={{ mb: 1.5 }}>
          <SymbolAutocomplete
            label="Symbol" value={symbol}
            onChange={(opt: any) => { setSymbol(opt?.symbol || ''); setExchange(opt?.exchange || ''); }}
          />
        </Box>
        <ToggleButtonGroup exclusive size="small" value={comparator} onChange={(_, v) => v && setComparator(v)} sx={{ mb: 1.5 }}>
          <ToggleButton value="above" sx={{ textTransform: 'none', px: 2 }}>Crosses above</ToggleButton>
          <ToggleButton value="below" sx={{ textTransform: 'none', px: 2 }}>Crosses below</ToggleButton>
        </ToggleButtonGroup>
        <TextField fullWidth size="small" type="number" label="Price" value={price} onChange={e => setPrice(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') submit(); }} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={submitting}>{submitting ? 'Setting…' : 'Set alert'}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default PriceAlertDialog;
